import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance';
import { useAuth } from "../context/AuthContext";
import RecipeEditModal from "../components/RecipeEditModal";
import CookingMode from "../components/CookingMode";
import ConfirmModal from "../components/ConfirmModal";
import Avatar from "../components/Avatar";
import { FaShareAlt, FaRss, FaCopy, FaExternalLinkAlt } from "react-icons/fa";
import SharePopup from "../components/SharePopup";
import useTimedMessage from "../hooks/useTimedMessage";
import { getApiErrorMessage } from "../lib/apiErrors";
import { toast } from "../lib/toast";

export default function RecipeDetailPage() {
  const { id } = useParams();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [selectedMedia, setSelectedMedia] = useState(null);
  const { currentUser } = useAuth();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showForkModal, setShowForkModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCookingMode, setShowCookingMode] = useState(false);
  const [inCart, setInCart] = useState(false);
  const [cartLoading, setCartLoading] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [checkedIngredients, setCheckedIngredients] = useState({});
  const [shareMsg, setShareMsg] = useTimedMessage(2000);
  const [showShareDropdown, setShowShareDropdown] = useState(false);
  const [showShareToFeed, setShowShareToFeed] = useState(false);
  const shareRef = useRef(null);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [commentsCount, setCommentsCount] = useState(0);
  const [loadingComments, setLoadingComments] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const thumbScrollRef = useRef(null);

  const handleMediaUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const data = new FormData();
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.type.startsWith("video/")) {
          if (file.size > 50 * 1024 * 1024) continue;
          data.append("videos", file);
        } else {
          data.append("images", file);
        }
      }
      await axiosInstance.patch(`/api/recipes/${id}/update/`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const refreshed = await axiosInstance.get(`/api/recipes/${id}/`);
      setRecipe(refreshed.data);
      setSelectedMedia(null);
      toast.success("Recipe media updated.");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to upload media."));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    const fetchRecipe = async () => {
      try {
        const res = await axiosInstance.get(`/api/recipes/${id}/`);
        const data = res.data;
        if (typeof data.instructions === 'string') data.instructions = JSON.parse(data.instructions);
        setRecipe(data);
        setLiked(res.data.is_liked || false);
        setLikesCount(res.data.likes_count || 0);
        setCommentsCount(res.data.comments_count || 0);
      } catch (err) {
        console.error('Failed to fetch recipe:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchRecipe();
  }, [id]);

  useEffect(() => {
    const checkCart = async () => {
      try {
        const res = await axiosInstance.get('/api/cart/');
        const recipeIds = res.data.items.map(item => item.recipe.id);
        setInCart(recipeIds.includes(parseInt(id)));
      } catch (err) {
        console.error('Failed to check cart:', err);
      }
    };
    checkCart();
  }, [id]);

  useEffect(() => {
    const fetchComments = async () => {
      setLoadingComments(true);
      try {
        const res = await axiosInstance.get(`/api/recipes/${id}/comments/`);
        setComments(res.data.results || []);
      } catch (err) {
        console.error("Failed to fetch comments:", err);
      } finally {
        setLoadingComments(false);
      }
    };
    fetchComments();
  }, [id]);

  const handleAddToCart = async () => {
    setCartLoading(true);
    try {
      // Send checked ingredients as excluded (user already has them)
      const excluded = recipe.recipe_ingredients
        .filter((_, idx) => checkedIngredients[idx])
        .map(item => item.original_text);
      await axiosInstance.post(`/api/cart/recipes/${recipe.id}/`, {
        excluded_ingredients: excluded,
      });
      setInCart(true);
      window.dispatchEvent(new Event("cart-changed"));
    } catch (err) {
      if (err.response?.status === 409) {
        setInCart(true);
      } else {
        console.error('Failed to add to cart:', err);
      }
    } finally {
      setCartLoading(false);
    }
  };

  const handleRemoveFromCart = async () => {
    setCartLoading(true);
    try {
      await axiosInstance.delete(`/api/cart/recipes/${recipe.id}/remove/`);
      setInCart(false);
      window.dispatchEvent(new Event("cart-changed"));
    } catch (err) {
      console.error('Failed to remove from cart:', err);
    } finally {
      setCartLoading(false);
    }
  };

  const handleForkSave = (forkedRecipe) => {
    navigate(`/recipes/${forkedRecipe.id}`);
  };

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/api/recipes/${recipe.id}/delete/`);
      navigate(-1);
    } catch (err) {
      console.error("Failed to delete recipe:", err);
    }
  };

  const handleLike = async () => {
    try {
      if (liked) {
        await axiosInstance.delete(`/api/recipes/${recipe.id}/like/`);
        setLiked(false);
        setLikesCount((c) => c - 1);
      } else {
        await axiosInstance.post(`/api/recipes/${recipe.id}/like/`);
        setLiked(true);
        setLikesCount((c) => c + 1);
      }
    } catch (err) {
      if (err.response?.status === 409) {
        setLiked(true);
      }
    }
  };

  // Close share dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (shareRef.current && !shareRef.current.contains(e.target)) {
        setShowShareDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCopyLink = async () => {
    const url = `${window.location.origin}/recipes/${recipe.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setShareMsg("Link copied!");

    } catch {
      setShareMsg("Failed to copy");

    }
    setShowShareDropdown(false);
  };

  const handleShareVia = async () => {
    const url = `${window.location.origin}/recipes/${recipe.id}`;
    try {
      await navigator.share({ title: recipe.title, url });
    } catch (err) {
      // User cancelled — ignore
    }
    setShowShareDropdown(false);
  };

  const handleShareToFeed = async (message) => {
    try {
      await axiosInstance.post(`/api/feed/share/${recipe.id}/`, { message });
      setShareMsg("Shared to feed!");

    } catch (err) {
      if (err.response?.status === 409) {
        setShareMsg("Already shared recently");
      } else if (err.response?.status === 403) {
        setShareMsg("Can only share your own recipes");
      } else {
        setShareMsg("Failed to share");
      }

    }
    setShowShareToFeed(false);
  };

  const handleDeleteImage = async (imageId) => {
    try {
      await axiosInstance.delete(`/api/images/${imageId}/`);
      setRecipe((prev) => {
        if (heroMedia?.id === imageId) {
          setSelectedMedia(null);
        }
        return { ...prev, images: prev.images.filter((img) => img.id !== imageId) };
      });
    } catch (err) {
      console.error("Failed to delete image:", err);
    }
  };

  const toggleIngredient = (idx) => {
    setCheckedIngredients((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    try {
      const res = await axiosInstance.post(`/api/recipes/${id}/comments/`, {
        text: commentText,
      });
      setComments((prev) => [res.data, ...prev]);
      setCommentText("");
      setCommentsCount((c) => c + 1);
    } catch (err) {
      console.error("Failed to post comment:", err);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await axiosInstance.delete(`/api/comments/${commentId}/`);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
      setCommentsCount((c) => c - 1);
    } catch (err) {
      console.error("Failed to delete comment:", err);
    }
  };

  const timeAgo = (dateString) => {
    const seconds = Math.floor((new Date() - new Date(dateString)) / 1000);
    if (seconds < 60) return "just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(dateString).toLocaleDateString();
  };

  const refreshRecipe = async () => {
    try {
      const res = await axiosInstance.get(`/api/recipes/${id}/`);
      const data = res.data;
      if (typeof data.instructions === 'string') data.instructions = JSON.parse(data.instructions);
      setRecipe(data);
      setLiked(data.is_liked || false);
      setLikesCount(data.likes_count || 0);
    } catch (err) {
      console.error("Failed to refresh recipe:", err);
    }
  };

  if (loading) return <div className="p-6 type-muted">Loading recipe...</div>;
  if (!recipe) return <div className="p-6 type-muted">Recipe not found</div>;

  const isOwner = currentUser?.id === recipe.owner?.id;
  const ownerName = recipe.owner?.first_name && recipe.owner?.last_name
    ? `${recipe.owner.first_name} ${recipe.owner.last_name}`
    : recipe.owner?.first_name || "Unknown";
  const sourceAuthorName = recipe.source_author_name?.trim() || "";
  const sourceUrl = recipe.source_url?.trim() || "";
  const hasExternalSource = Boolean(sourceAuthorName || sourceUrl);
  // Sort media: videos first, newest first within each group
  const sortedMedia = [...(recipe.images || [])].sort((a, b) => {
    if (a.media_type !== b.media_type) return a.media_type === 'video' ? -1 : 1;
    return b.id - a.id;
  });
  const heroMedia = selectedMedia || sortedMedia[0] || null;

  return (
    <div className="max-w-3xl mx-auto p-6 pb-12">
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleMediaUpload}
        accept="image/*,video/mp4,video/webm,video/quicktime"
        multiple
        className="hidden"
      />

      {/* Hero Image */}
      <div className="rounded-2xl overflow-hidden bg-gray-100 dark:bg-gray-800">
        {heroMedia?.media_type === 'video' && heroMedia?.video ? (
          <video
            key={heroMedia.id}
            src={heroMedia.video}
            className="w-full h-80 md:h-96 object-contain bg-black"
            controls
            autoPlay
            muted
            playsInline
          />
        ) : heroMedia?.image ? (
          <img
            src={heroMedia.image}
            alt={recipe.title}
            className="w-full h-80 md:h-96 object-cover"
          />
        ) : isOwner ? (
          <div
            className="w-full h-80 md:h-96 flex flex-col items-center justify-center text-gray-400 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition"
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? (
              <span className="text-sm">Uploading...</span>
            ) : (
              <>
                <svg className="w-12 h-12 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
                </svg>
                <span className="text-sm">Click to upload a photo</span>
              </>
            )}
          </div>
        ) : (
          <div className="w-full h-80 md:h-96 flex items-center justify-center text-gray-400 text-lg">
            No Image Available
          </div>
        )}
      </div>

      {/* Below hero: thumbnails (left) + action buttons (right) */}
      {/* Thumbnails full width */}
      <div className="flex items-center space-x-1 mt-3">
        {sortedMedia.length > 3 && (
          <button
            onClick={() => thumbScrollRef.current?.scrollBy({ left: -120, behavior: 'smooth' })}
            className="flex-shrink-0 w-6 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}
        <div ref={thumbScrollRef} className="flex items-center space-x-1.5 overflow-x-hidden min-w-0 flex-1">
          {sortedMedia.map((item) => (
            <div key={item.id} className="flex-shrink-0 relative">
              {item.media_type === 'video' ? (
                <div
                  className={`w-12 h-12 rounded-lg cursor-pointer transition bg-gray-800 flex items-center justify-center ${
                    heroMedia?.id === item.id
                      ? "ring-2 ring-blue-500"
                      : "opacity-60 hover:opacity-100"
                  }`}
                  onClick={() => setSelectedMedia(item)}
                >
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              ) : (
                <img
                  src={item.image}
                  alt="Thumbnail"
                  className={`w-12 h-12 object-cover rounded-lg cursor-pointer transition ${
                    heroMedia?.id === item.id
                      ? "ring-2 ring-blue-500"
                      : "opacity-60 hover:opacity-100"
                  }`}
                  onClick={() => setSelectedMedia(item)}
                />
              )}
            </div>
          ))}
        </div>
        {sortedMedia.length > 3 && (
          <button
            onClick={() => thumbScrollRef.current?.scrollBy({ left: 120, behavior: 'smooth' })}
            className="flex-shrink-0 w-6 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
        {isOwner && (
          <div className="flex items-center space-x-1 flex-shrink-0 ml-1">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-400 hover:bg-blue-100 hover:text-blue-500 dark:hover:bg-blue-900/30 transition text-xs"
            >
              {uploading ? "..." : "+"}
            </button>
            {sortedMedia.length > 0 && (
              <button
                onClick={() => {
                  if (heroMedia) handleDeleteImage(heroMedia.id);
                }}
                className="w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-400 hover:bg-red-100 hover:text-red-500 dark:hover:bg-red-900/30 transition text-xs"
              >
                -
              </button>
            )}
          </div>
        )}
      </div>

      {/* Title & Description */}
      <h1 className="type-page-title mt-6">{recipe.title}</h1>
      {recipe.description && (
        <p className="type-body mt-2 leading-relaxed">{recipe.description}</p>
      )}

      {/* Author + Forked From */}
      <div className="flex items-center mt-4 space-x-3">
        <Link to={`/profile/${recipe.owner?.id}`} className="flex-shrink-0">
          <Avatar user={recipe.owner} size="sm" />
        </Link>
        <div className="text-sm">
          <span className="text-gray-400 italic">crafted by</span>{' '}
          <Link to={`/profile/${recipe.owner?.id}`} className="font-semibold text-gray-800 dark:text-white hover:underline">{ownerName}</Link>
          <span className="text-gray-300 dark:text-gray-600 mx-1.5">·</span>
          <span className="text-gray-400">{timeAgo(recipe.created_at)}</span>
          {(recipe.forked_from || ( recipe.original_author && recipe.original_author.id !== recipe.owner?.id)) && (
            <>
              <span className="text-gray-300 dark:text-gray-600 mx-1.5">·</span>
              <span className="italic text-gray-400">inspired by</span>{' '}
              {recipe.forked_from ? (
                <Link to={`/recipes/${recipe.forked_from}`} className="text-blue-500 hover:underline font-semibold not-italic">
                {recipe.original_author?.first_name} {recipe.original_author?.last_name}'s recipe
                </Link>
              ) : (
                <span className="font-semibold text-gray-600 dark:text-gray-300">
                  {recipe.original_author?.first_name} {recipe.original_author?.last_name}'s recipe
                </span>
              )}
            </>
          )}
          {hasExternalSource && (
            <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
              {sourceUrl ? (
                <a
                  href={sourceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-blue-500 italic hover:text-blue-600 hover:underline"
                >
                  original recipe
                  <FaExternalLinkAlt className="h-3 w-3 not-italic" />
                </a>
              ) : (
                <span className="text-gray-400 italic">original recipe</span>
              )}
              {sourceAuthorName && (
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  by {sourceAuthorName}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tags */}
      {recipe.tags?.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4">
          {recipe.tags.map((tag) => (
            <Link
              key={tag.id}
              to={`/recipes?view=discover&tag=${tag.name}`}
              className="inline-block bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-medium px-2.5 py-1 rounded-full hover:bg-blue-200 dark:hover:bg-blue-900/60 transition"
            >
              #{tag.name}
            </Link>
          ))}
        </div>
      )}

      {/* Info Pills */}
      <div className="flex flex-wrap items-center gap-3 mt-5 py-3 border-t border-b dark:border-gray-700">
        {recipe.prep_time && (
          <div className="bg-gray-50 dark:bg-gray-800 border dark:border-gray-700 rounded-xl px-4 py-2 text-center">
            <div className="text-sm font-semibold text-gray-800 dark:text-white">{recipe.prep_time} min</div>
            <div className="text-xs text-gray-400">Prep</div>
          </div>
        )}
        {recipe.cook_time && (
          <div className="bg-gray-50 dark:bg-gray-800 border dark:border-gray-700 rounded-xl px-4 py-2 text-center">
            <div className="text-sm font-semibold text-gray-800 dark:text-white">{recipe.cook_time} min</div>
            <div className="text-xs text-gray-400">Cook</div>
          </div>
        )}
        {recipe.servings && (
          <div className="bg-gray-50 dark:bg-gray-800 border dark:border-gray-700 rounded-xl px-4 py-2 text-center">
            <div className="text-sm font-semibold text-gray-800 dark:text-white">{recipe.servings}</div>
            <div className="text-xs text-gray-400">Servings</div>
          </div>
        )}
        {recipe.difficulty && (
          <div className="bg-gray-50 dark:bg-gray-800 border dark:border-gray-700 rounded-xl px-4 py-2 text-center">
            <div className="text-sm font-semibold text-gray-800 dark:text-white">
              {recipe.difficulty.charAt(0).toUpperCase() + recipe.difficulty.slice(1)}
            </div>
            <div className="text-xs text-gray-400">Difficulty</div>
          </div>
        )}
        {recipe.category && (
          <div className="bg-gray-50 dark:bg-gray-800 border dark:border-gray-700 rounded-xl px-4 py-2 text-center">
            <div className="text-sm font-semibold text-gray-800 dark:text-white">{recipe.category}</div>
            <div className="text-xs text-gray-400">Category</div>
          </div>
        )}
        {recipe.diet_preference && (
          <div className="bg-gray-50 dark:bg-gray-800 border dark:border-gray-700 rounded-xl px-4 py-2 text-center">
            <div className="text-sm font-semibold text-gray-800 dark:text-white">{recipe.diet_preference}</div>
            <div className="text-xs text-gray-400">Diet</div>
          </div>
        )}
      </div>

      {/* Ingredients */}
      <div className="mt-8">
        <h2 className="type-section-title mb-4">Ingredients</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
          {recipe.recipe_ingredients?.map((item, idx) => (
            <label
              key={idx}
              className="flex items-center space-x-3 cursor-pointer group"
              onClick={() => toggleIngredient(idx)}
            >
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition ${
                checkedIngredients[idx]
                  ? "bg-blue-500 border-blue-500"
                  : "border-gray-300 dark:border-gray-600 group-hover:border-blue-300"
              }`}>
                {checkedIngredients[idx] && (
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <span className="text-gray-700 dark:text-gray-300">
                {item.original_text}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Instructions */}
      <div className="mt-8">
        <h2 className="type-section-title mb-4">Instructions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          {recipe.instructions.map((step, idx) => (
            <div key={idx} className="flex space-x-4">
              <div className="flex-shrink-0 text-gray-800 dark:text-white text-base">
                {idx + 1}.
              </div>
              <p className="text-gray-700 dark:text-gray-300 pt-1 leading-relaxed">{step}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex space-x-3 mt-8">
        {isOwner ? (
          <>
            <button
              className="flex-1 py-3 rounded-lg text-sm font-medium text-blue-600 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition"
              onClick={() => setShowEditModal(true)}
            >
              Edit Recipe
            </button>
            <button
              onClick={inCart ? handleRemoveFromCart : handleAddToCart}
              disabled={cartLoading}
              className={`flex-1 py-3 rounded-lg text-sm font-medium transition ${
                inCart ? "text-orange-500 bg-orange-50 dark:bg-orange-900/30 hover:bg-orange-100 dark:hover:bg-orange-900/50" : "text-white bg-blue-500 hover:bg-blue-600"
              }`}
            >
              {cartLoading ? '...' : inCart ? 'In Cart' : 'Add to Cart'}
            </button>
            <button
              className="flex-1 py-3 rounded-lg text-sm font-medium text-green-600 bg-green-50 dark:bg-green-900/30 hover:bg-green-100 dark:hover:bg-green-900/50 transition"
              onClick={() => setShowCookingMode(true)}
            >
              Cook It!
            </button>
          </>
        ) : (
          <>
            <div className="relative flex-1 group">
              <button
                className={`w-full py-3 rounded-lg text-sm font-medium transition ${
                  recipe.is_forked || recipe.is_own_derivative
                    ? "text-gray-400 bg-gray-100 dark:bg-gray-700 cursor-not-allowed"
                    : "text-green-600 bg-green-50 dark:bg-green-900/30 hover:bg-green-100 dark:hover:bg-green-900/50"
                }`}
                onClick={() => !recipe.is_forked && !recipe.is_own_derivative && setShowForkModal(true)}
                disabled={recipe.is_forked || recipe.is_own_derivative}
              >
                {recipe.is_forked ? "Already Forked" : recipe.is_own_derivative ? "Derivative of Yours" : "Fork Recipe"}
              </button>
              {recipe.is_own_derivative && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-800 dark:bg-gray-700 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  This recipe originated from yours
                </div>
              )}
            </div>
            <button
              onClick={inCart ? handleRemoveFromCart : handleAddToCart}
              disabled={cartLoading}
              className={`flex-1 py-3 rounded-lg text-sm font-medium transition ${
                inCart ? "text-orange-500 bg-orange-50 dark:bg-orange-900/30 hover:bg-orange-100 dark:hover:bg-orange-900/50" : "text-white bg-blue-500 hover:bg-blue-600"
              }`}
            >
              {cartLoading ? '...' : inCart ? 'In Cart' : 'Add to Cart'}
            </button>
            <button
              className="flex-1 py-3 rounded-lg text-sm font-medium text-green-600 bg-green-50 dark:bg-green-900/30 hover:bg-green-100 dark:hover:bg-green-900/50 transition"
              onClick={() => setShowCookingMode(true)}
            >
              Cook It!
            </button>
          </>
        )}
      </div>

      {/* Comments Section */}
      <div className="mt-8 border-t dark:border-gray-700 pt-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleLike}
              className={`flex items-center space-x-1.5 px-4 py-2 rounded-lg text-sm font-medium transition ${
                liked ? "text-red-500 bg-red-50 dark:bg-red-900/30" : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
              }`}
            >
              <span>{liked ? "Liked" : "Like"}</span>
              <span className="text-xs">({likesCount})</span>
            </button>
            <div className="relative" ref={shareRef}>
              <button
                onClick={() => setShowShareDropdown(!showShareDropdown)}
                className="flex items-center space-x-1.5 px-4 py-2 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
              >
                <FaShareAlt size={14} />
                <span>Share</span>
              </button>
              {shareMsg && (
                <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                  {shareMsg}
                </span>
              )}
              {showShareDropdown && (
                <div className="absolute top-full mt-1 left-0 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg shadow-lg py-1 z-10 min-w-[180px]">
                  {isOwner && recipe.visibility === "public" && (
                    <button
                      onClick={() => { setShowShareDropdown(false); setShowShareToFeed(true); }}
                      className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                    >
                      <FaRss size={13} />
                      <span>Share to Feed</span>
                    </button>
                  )}
                  <button
                    onClick={handleCopyLink}
                    className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                  >
                    <FaCopy size={13} />
                    <span>Copy Link</span>
                  </button>
                  {navigator.share && (
                    <button
                      onClick={handleShareVia}
                      className="w-full flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                    >
                      <FaExternalLinkAlt size={13} />
                      <span>Share via...</span>
                    </button>
                  )}
                </div>
              )}
            </div>
            <h2 className="type-section-title">
              Comments ({commentsCount})
            </h2>
          </div>
        </div>

        {/* Comment Input */}
        <form onSubmit={handleSubmitComment} className="flex items-center space-x-3 mb-6">
          <Avatar user={currentUser} size="sm" className="flex-shrink-0" />
          <input
            type="text"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Write a comment..."
            maxLength={500}
            className="flex-1 border rounded-full px-4 py-2 text-sm focus:outline-none focus:border-blue-400 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
          />
          <button
            type="submit"
            disabled={!commentText.trim()}
            className="text-blue-500 font-semibold text-sm disabled:text-gray-300 dark:disabled:text-gray-600"
          >
            Post
          </button>
        </form>

        {/* Comments List */}
        <div className="space-y-4">
          {loadingComments ? (
            <p className="type-muted">Loading comments...</p>
          ) : comments.length === 0 ? (
            <p className="type-muted">No comments yet.</p>
          ) : (
            comments.map((comment) => (
              <div key={comment.id} className="flex items-start space-x-2">
                <div className="flex-shrink-0">
                  <Avatar user={comment.user} size="xs" linkToProfile />
                </div>
                <div className="flex-1">
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2">
                    <Link
                      to={`/profile/${comment.user?.id}`}
                      className="font-semibold text-sm text-gray-800 dark:text-white hover:underline"
                    >
                      {comment.user?.first_name
                        ? `${comment.user.first_name} ${comment.user.last_name}`
                        : "User"}
                    </Link>
                    <p className="text-sm text-gray-700 dark:text-gray-300">{comment.text}</p>
                  </div>
                  <div className="flex items-center space-x-3 mt-1 text-xs text-gray-400">
                    <span>{timeAgo(comment.created_at)}</span>
                    {currentUser?.id === comment.user?.id && (
                      <button
                        onClick={() => handleDeleteComment(comment.id)}
                        className="text-red-400 hover:text-red-600"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Delete Recipe — owner only, at bottom */}
      {isOwner && (
        <div className="mt-12 pt-6 border-t dark:border-gray-700">
          <button
            className="text-sm text-red-500 hover:text-red-600 transition"
            onClick={() => setShowDeleteConfirm(true)}
          >
            Delete Recipe
          </button>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <RecipeEditModal
          recipe={recipe}
          onClose={() => setShowEditModal(false)}
          onSave={() => { refreshRecipe(); setShowEditModal(false); }}
        />
      )}

      {/* Fork Modal */}
      {showForkModal && (
        <RecipeEditModal
          recipe={recipe}
          mode="fork"
          onClose={() => setShowForkModal(false)}
          onSave={handleForkSave}
        />
      )}

      {/* Share to Feed Modal */}
      {showShareToFeed && (
        <SharePopup
          title="Share to Feed"
          description="Share this recipe to your followers' feed. Add an optional message:"
          confirmText="Share to Feed"
          onConfirm={handleShareToFeed}
          onCancel={() => setShowShareToFeed(false)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <ConfirmModal
          title="Delete Recipe"
          message={<>Are you sure you want to delete <span className="font-semibold text-gray-700 dark:text-gray-300">"{recipe.title}"</span>? This will remove all images, likes, and comments. This cannot be undone.</>}
          confirmText="Delete"
          variant="danger"
          onConfirm={() => { setShowDeleteConfirm(false); handleDelete(); }}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}

      {showCookingMode && recipe && (
        <CookingMode recipe={recipe} onClose={() => setShowCookingMode(false)} />
      )}
    </div>
  );
}
